import type { FormattedExecutionResult, GraphQLFormattedError } from "graphql";

import type { ApolloLink } from "@apollo/client/link";
import type { DeepPartial, HKT } from "@apollo/client/utilities";
import {
  DeepMerger,
  hasDirectives,
  isNonEmptyArray,
} from "@apollo/client/utilities/internal";

import type { Incremental } from "../types.js";

export declare namespace Defer20220824Handler {
  interface Defer20220824Result extends HKT {
    arg1: unknown; // TData
    arg2: unknown; // TExtensions
    return: Defer20220824Handler.Chunk<Record<string, unknown>>;
  }
  export interface TypeOverrides {
    AdditionalApolloLinkResultTypes: Defer20220824Result;
  }

  export type InitialResult<TData = Record<string, unknown>> = {
    data?: TData | null | undefined;
    errors?: ReadonlyArray<GraphQLFormattedError>;
    extensions?: Record<string, unknown>;
    hasNext: boolean;
    incremental?: ReadonlyArray<IncrementalResult<TData>>;
  };

  export type SubsequentResult<TData = Record<string, unknown>> = {
    extensions?: Record<string, unknown>;
    hasNext: boolean;
    incremental?: Array<IncrementalResult<TData>>;
  };

  export type IncrementalDeferResult<TData = Record<string, unknown>> = {
    data?: TData | null;
    errors?: ReadonlyArray<GraphQLFormattedError>;
    extensions?: Record<string, unknown>;
    path?: Incremental.Path;
    label?: string;
  };

  export type IncrementalStreamResult<TData = Array<unknown>> = {
    errors?: ReadonlyArray<GraphQLFormattedError>;
    items?: TData;
    path?: Incremental.Path;
    label?: string;
    extensions?: Record<string, unknown>;
  };

  export type IncrementalResult<TData = Record<string, unknown>> =
    | IncrementalDeferResult<TData>
    | IncrementalStreamResult<TData>;

  export type Chunk<TData extends Record<string, unknown>> =
    | InitialResult<TData>
    | SubsequentResult<TData>;
}

class DeferRequest<TData extends Record<string, unknown>>
  implements
    Incremental.IncrementalRequest<Defer20220824Handler.Chunk<TData>, TData>
{
  private hasDefer: boolean;
  public hasNext = true;
  private hasFailedDefer = false;

  constructor({ query }: Incremental.StartRequestOptions) {
    this.hasDefer = hasDirectives(["defer"], query);
  }

  private errors: Array<GraphQLFormattedError> = [];
  private extensions: Record<string, any> = {};
  private data: any = {};
  // This tracks paths for `@stream` arrays that returns items: null due to
  // errors thrown for non-null array items. We stop processing future updates
  // to these stream arrays to prevent creating sparse arrays or inserting
  // `null` for an expected non-null value which could cause runtime crashes.
  private ignoredImpossibleStreamPaths = new Set<string>();

  private merge(
    normalized: FormattedExecutionResult<TData>,
    atPath?: DeepMerger.MergeOptions["atPath"]
  ) {
    if (normalized.data !== undefined) {
      this.data = new DeepMerger({ arrayMerge: "truncate" }).merge(
        this.data,
        normalized.data,
        { atPath }
      );
    }
    if (normalized.errors) {
      this.errors.push(...normalized.errors);
    }
    Object.assign(this.extensions, normalized.extensions);
  }

  handle(
    // we'll get `undefined` here in case of a `no-cache` fetch policy,
    // so we'll continue with the last value this request had accumulated
    cacheData: TData | DeepPartial<TData> | null | undefined = this.data,
    chunk: Defer20220824Handler.Chunk<TData>
  ): FormattedExecutionResult<TData> {
    this.hasNext = chunk.hasNext;
    this.data = cacheData;

    if (hasIncrementalChunks(chunk)) {
      for (const incremental of chunk.incremental) {
        const { path, errors, extensions } = incremental;

        if ("items" in incremental) {
          // Remove the array index from the end of the array since each future
          // chunk sends a different array index. This normalizes the path to
          // ensure we ignore updates to this field if `items` is `null`.
          const stringPath = path?.slice(0, -1).join(".") ?? "";

          if (incremental.items === null) {
            this.ignoredImpossibleStreamPaths.add(stringPath);
          }

          if (this.ignoredImpossibleStreamPaths.has(stringPath)) {
            this.merge({ errors, extensions });
            continue;
          }
        }

        if ("data" in incremental && incremental.data === null) {
          this.hasFailedDefer = true;
        }

        let data: any =
          "items" in incremental ? incremental.items
            // Ensure `data: null` isn't merged for `@defer` responses by
            // falling back to `undefined`
          : "data" in incremental ? incremental.data ?? undefined
          : undefined;

        if (path && typeof path.at(-1) === "number" && Array.isArray(data)) {
          const startingIdx = path.at(-1) as number;
          data.forEach((item, idx) => {
            this.merge({ data: item }, [
              ...path!.slice(0, -1),
              startingIdx + idx,
            ]);
          });
        } else {
          this.merge({ data }, path);
        }

        this.merge({ errors, extensions });
      }
    } else {
      this.merge(chunk);
    }

    const result: FormattedExecutionResult<TData> = { data: this.data };

    if (isNonEmptyArray(this.errors)) {
      result.errors = this.errors;
    }

    if (Object.keys(this.extensions).length > 0) {
      result.extensions = this.extensions;
    }

    return result;
  }

  // This protocol can't identify individual `@defer`/`@stream` boundaries by
  // `path`/`label`, so we can't answer whether a *specific* boundary is
  // pending - only whether *anything* might still be coming (`hasNext`), or
  // whether a boundary failed and will never arrive (`hasFailedDefer`).
  isDeferPending = () => this.hasDefer && (this.hasNext || this.hasFailedDefer);
}

/**
 * This handler implements the `@defer` directive as specified in this historical commit:
 * https://github.com/graphql/graphql-spec/tree/48cf7263a71a683fab03d45d309fd42d8d9a6659/spec
 */
export class Defer20220824Handler
  implements Incremental.Handler<Defer20220824Handler.Chunk<any>>
{
  isIncrementalResult(
    result: Record<string, any>
  ): result is
    | Defer20220824Handler.SubsequentResult
    | Defer20220824Handler.InitialResult {
    return "hasNext" in result;
  }

  extractErrors(result: ApolloLink.Result<any>) {
    const acc: GraphQLFormattedError[] = [];
    const push = ({
      errors,
    }: {
      errors?: ReadonlyArray<GraphQLFormattedError>;
    }) => {
      if (errors) {
        acc.push(...errors);
      }
    };
    if (this.isIncrementalResult(result)) {
      if ("errors" in result) {
        push(result);
      }
      if (hasIncrementalChunks(result)) {
        result.incremental.forEach(push);
      }
    }
    if (acc.length) {
      return acc;
    }
  }

  prepareRequest(request: ApolloLink.Request): ApolloLink.Request {
    if (hasDirectives(["defer", "stream"], request.query)) {
      const context = request.context ?? {};
      const http = (context.http ??= {});
      http.accept = [
        "multipart/mixed;deferSpec=20220824",
        ...(http.accept || []),
      ];
    }

    return request;
  }
  startRequest<TData extends Record<string, unknown>>(
    opts: Incremental.StartRequestOptions
  ) {
    return new DeferRequest<TData>(opts);
  }
}

// only exported for use in tests
export function hasIncrementalChunks(
  result: Record<string, any>
): result is Required<Defer20220824Handler.SubsequentResult> {
  return isNonEmptyArray(result.incremental);
}
